import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  action?: ToastAction;
  duration?: number;
}

export interface ToastOptions {
  type?: ToastType;
  action?: ToastAction;
  duration?: number;
}

export interface ErrorToastOptions {
  /** Retry callback function */
  onRetry?: () => void;
  /** Custom retry button label */
  retryLabel?: string;
  /** Toast duration in ms (default: 6000 for retryable, 8000 for non-retryable) */
  duration?: number;
}

interface ToastContextValue {
  toasts: Toast[];
  addToast: (message: string, options?: ToastOptions) => string;
  /**
   * Add an error toast with optional retry action
   * Error toasts have longer duration and can include retry buttons
   */
  addErrorToast: (message: string, options?: ErrorToastOptions) => string;
  removeToast: (id: string) => void;
  clearAllToasts: () => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_DURATION = 4000;
const ERROR_DURATION = 6000;
const ERROR_LONG_DURATION = 8000;

let toastIdCounter = 0;

function generateToastId(): string {
  return `toast-${++toastIdCounter}-${Date.now()}`;
}

export interface ToastProviderProps {
  children: ReactNode;
}

/**
 * Provider component for the toast notification system.
 * Wrap your app with this to enable toast notifications.
 *
 * @example
 * ```tsx
 * <ToastProvider>
 *   <App />
 * </ToastProvider>
 * ```
 */
export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback(
    (message: string, options: ToastOptions = {}): string => {
      const id = generateToastId();
      const toast: Toast = {
        id,
        message,
        type: options.type ?? 'info',
        action: options.action,
        duration: options.duration ?? DEFAULT_DURATION,
      };

      setToasts((prev) => [...prev, toast]);
      return id;
    },
    []
  );

  const addErrorToast = useCallback(
    (message: string, options: ErrorToastOptions = {}): string => {
      const { onRetry, retryLabel = 'Retry', duration } = options;
      const id = generateToastId();

      const toast: Toast = {
        id,
        message,
        type: 'error',
        action: onRetry
          ? {
              label: retryLabel,
              onClick: onRetry,
            }
          : undefined,
        // Use longer duration for errors, even longer if no retry available
        duration: duration ?? (onRetry ? ERROR_DURATION : ERROR_LONG_DURATION),
      };

      setToasts((prev) => [...prev, toast]);
      return id;
    },
    []
  );

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const value = useMemo(
    () => ({
      toasts,
      addToast,
      addErrorToast,
      removeToast,
      clearAllToasts,
    }),
    [toasts, addToast, addErrorToast, removeToast, clearAllToasts]
  );

  return (
    <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
  );
}

/**
 * Hook to access toast notifications.
 *
 * @example
 * ```tsx
 * const { addToast } = useToast();
 *
 * // Simple toast
 * addToast('Song added to queue!');
 *
 * // Toast with type
 * addToast('Failed to load songs', { type: 'error' });
 *
 * // Toast with action button
 * addToast('Song removed', {
 *   type: 'success',
 *   action: {
 *     label: 'Undo',
 *     onClick: () => restoreSong()
 *   }
 * });
 * ```
 */
export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
